import React, { useState, useEffect } from "react";
import PrimaryButton from "../components/buttons/PrimaryButton";
import "./BatchBar.css";

const BatchBar = ({
  selectedVerbatimsCount,
  onApplyLabel,
  availableLabels,
  labelTypes,
  onLabelSelect,
  onFocusEdit,
  resetBatchBarFields = false
}) => {
  const [selectedLabelType, setSelectedLabelType] = useState(""); // Track selected label type
  const [selectedLabel, setSelectedLabel] = useState(""); // Track selected label
  const [selectedSentimentState, setSelectedSentimentState] = useState(""); // Track sentiment state if needed
  const [selectedAction, setSelectedAction] = useState(""); // Track action for multi-value label types
  
  // Handle when a new label type is selected
  const handleLabelTypeChange = (e) => {
    const labelType = e.target.value;
    setSelectedLabelType(labelType); // Update selected label type
    setSelectedLabel(""); // Reset selected label when label type changes
    setSelectedSentimentState(""); // Reset sentiment state when label type changes
  };

  // Handle label change
  const handleLabelChange = (e) => {
    setSelectedLabel(e.target.value);
    onLabelSelect(e.target.value); // Pass selected label back to parent
  };

  // Handle sentiment state change
  const handleSentimentChange = (e) => {
    setSelectedSentimentState(e.target.value);
  };

  // Handle action change for multi-value label types
  const handleActionChange = (e) => {
    setSelectedAction(e.target.value);
  };

  useEffect(() => {
    if (resetBatchBarFields) {
      // Reset all fields when resetBatchBarFields is true
      setSelectedLabelType("");
      setSelectedLabel("");
      setSelectedSentimentState("");
      setSelectedAction("");
    }
  }, [resetBatchBarFields]);

  // Extract the label types (keys from the availableLabels object)
  const labelTypesKeys = Object.keys(availableLabels || {});

  // Get the labels for the selected label type
  const filteredLabels = selectedLabelType
    ? availableLabels[selectedLabelType] || []
    : [];

  // Check if the selected label type is sentiment-enabled in labelTypes
  const isSentimentEnabled = labelTypes[selectedLabelType]?.sentimentEnabled;
  // Determine if the selected label type supports multi-value labels
  const isMultiValueLabelType =
    labelTypes[selectedLabelType]?.multiValue || false;
  // Check if all required fields are selected to enable buttons
  const areRequiredFieldsSelected =
    selectedLabelType &&
    selectedLabel &&
    (!isSentimentEnabled || selectedSentimentState) && 
    (isMultiValueLabelType ? selectedAction : true);

  return (
    <div className="batch-bar">
      <div>
        <h2>Batch Editing</h2>
      </div>
      <p>Selected Verbatims: {selectedVerbatimsCount}</p>

      <h3>To apply labels in batch</h3>
      {/* Dropdown for selecting label type */}
      <div className="batch-select-section">
        <div className="label-type-selector">
          <select
            className="batch-label-select"
            value={selectedLabelType}
            onChange={handleLabelTypeChange}
          >
            <option value="">Select Label Type</option>
            {labelTypesKeys.map((labelType) => (
              <option key={labelType} value={labelType}>
                {labelType}
              </option>
            ))}
          </select>
        </div>

        {/* Dropdown for selecting label within the selected label type */}
        <div className="label-selector">
          <select
            className="batch-label-select" /* Apply custom styling */
            value={selectedLabel}
            onChange={handleLabelChange}
            disabled={!selectedLabelType}
          >
            <option value="">Select Label</option>
            {filteredLabels.map((label) => (
              <option key={label.name} value={label.name}>
                {label.name}
              </option>
            ))}
          </select>
        </div>

        {/* Conditionally render sentiment state selector */}
        {isSentimentEnabled && (
          <div className="sentiment-selector">
            <select
              className="batch-label-select"
              value={selectedSentimentState}
              onChange={handleSentimentChange}
              disabled={!selectedLabel}
            >
              <option value="">Select Sentiment State</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>
        )}

        {/* Conditionally render multi-value label selector */}
        {isMultiValueLabelType && (
          <div className="sentiment-selector">
            <select
              className="batch-label-select"
              value={selectedAction}
              onChange={handleActionChange}
              disabled={!selectedLabel}
            >
              <option value="">Select action</option>
              <option value="add">Add</option>
              <option value="remove">Remove</option>
            </select>
          </div>
        )}
      </div>

      <div className="batch-actions">
        <div>
          {/* Button to apply selected label */}
          <PrimaryButton
            onClick={() =>
              onApplyLabel(
                selectedLabelType,
                selectedLabel,
                selectedSentimentState,
                selectedAction
              )
            }
            disabled={
              selectedVerbatimsCount === 0 || !areRequiredFieldsSelected
            }
          >
            Immediately Apply
          </PrimaryButton>
        </div>

        <div>
          {/* New "Focus Editing" Button */}
          <PrimaryButton
            onClick={() =>
              onFocusEdit(
                selectedLabelType,
                selectedLabel,
                selectedSentimentState, // Pass sentiment state
                selectedAction
              )
            }
            disabled={
              selectedVerbatimsCount === 0 || !areRequiredFieldsSelected
            }
          >
            Switch to Focused Mode
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default BatchBar;
